import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookCheck, CheckCircle2, Search } from 'lucide-react';
import { toast } from 'sonner';

import { useLibraryOrg } from '@/context/LibraryOrgContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LibraryPublicReturnPage = () => {
  const org = useLibraryOrg();
  const [email, setEmail] = useState('');
  const [loans, setLoans] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reportedTitle, setReportedTitle] = useState('');

  const lookupLoans = async (event) => {
    event.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setReportedTitle('');
    try {
      const response = await axios.post(`${API}/public/library/${org.id}/returns/lookup`, { borrower_email: email.trim() });
      setLoans(response.data || []);
      setSearched(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Uitleningen zoeken mislukt');
    } finally {
      setLoading(false);
    }
  };

  const reportReturn = async (loan) => {
    setLoading(true);
    try {
      await axios.post(`${API}/public/library/${org.id}/returns/report`, {
        borrower_email: email.trim(), loan_id: loan.loan_id,
      });
      setLoans((current) => current.filter((item) => item.loan_id !== loan.loan_id));
      setReportedTitle(loan.book_title);
      toast.success('Inlevering gemeld');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Inlevering melden mislukt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="mx-auto max-w-xl">
        <Link to={`/${org.slug}/bibliotheek`} className="mb-6 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" /> Terug naar bibliotheek</Link>
        <div className="rounded-2xl bg-white p-6 shadow-sm">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-teal-700"><BookCheck className="h-6 w-6" /></div>
          <h1 className="mt-4 text-2xl font-bold text-slate-900">Boek inleveren</h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">Leg het boek eerst terug bij de boekenkast. Meld daarna hieronder de inlevering. Een bibliotheekbeheerder controleert het boek voordat het opnieuw beschikbaar wordt.</p>

          <form onSubmit={lookupLoans} className="mt-6 space-y-3">
            <div className="space-y-2"><Label>E-mailadres waarmee je leende</Label><Input type="email" required value={email} onChange={(event) => setEmail(event.target.value)} placeholder="naam@voorbeeld.nl" className="rounded-xl" /></div>
            <Button type="submit" disabled={loading} className="w-full rounded-xl bg-teal-600 text-white hover:bg-teal-700"><Search className="mr-2 h-4 w-4" />{loading ? 'Zoeken…' : 'Zoek mijn uitleningen'}</Button>
          </form>

          {reportedTitle && <div className="mt-5 flex gap-3 rounded-xl bg-emerald-50 p-4 text-sm text-emerald-800"><CheckCircle2 className="h-5 w-5 shrink-0" /><div><p className="font-semibold">Inlevering gemeld</p><p>De beheerder controleert “{reportedTitle}” en maakt het daarna weer beschikbaar.</p></div></div>}

          {searched && <div className="mt-6 space-y-3">
            {loans.map((loan) => <div key={loan.loan_id} className="rounded-xl border border-slate-200 p-4"><p className="font-semibold text-slate-900">{loan.book_title}</p><p className="mt-1 text-sm text-slate-500">Geleend op {loan.loan_date} · terug vóór {loan.due_date}</p><Button type="button" variant="outline" disabled={loading} onClick={() => reportReturn(loan)} className="mt-3 w-full rounded-xl">Ik heb dit boek teruggelegd</Button></div>)}
            {!loans.length && !reportedTitle && <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">Geen actieve uitleningen gevonden voor dit e-mailadres.</p>}
          </div>}
        </div>
      </div>
    </main>
  );
};

export default LibraryPublicReturnPage;
